import { Component, OnDestroy, OnInit } from '@angular/core'
import { Router } from '@angular/router'
import { $app, AppService } from '@app/app.service'
import { User } from '@data/model/user.model'
import { UserService } from '@data/service/user.service'
import * as StageActions from '@data/store/stage/stage.actions'
import { setProject } from '@data/store/stage/stage.actions'
import { getProject } from '@data/store/stage/stage.selectors'
import { AppState } from '@data/store/storage.module'
import { Store } from '@ngrx/store'
import { KeycloakProfile } from 'keycloak-js'
import { from, of, Subscription } from 'rxjs'
import { catchError, filter, tap } from 'rxjs/operators'

@Component({
   selector: 'app-root',
   templateUrl: './app.component.html',
})
export class AppComponent implements OnInit, OnDestroy {
   console: boolean = $app.core.modeDevel
   logPrefix: string = '@app - ' + this.constructor.name

   $app = $app

   private readonly _sub = new Subscription()

   constructor(
      private readonly userSrv: UserService,
      private readonly _store: Store<AppState>,
      private readonly _router: Router,
      private readonly _appService: AppService
   ) {}

   ngOnInit() {
      if (this.console) console.log(this.logPrefix, 'ngOnInit')

      $app.core.appData = {
         currentLayout: null,
      }

      this._appService.setupLayoutAndTableElements()

      $app.core.stage.applyLayoutTheme('DEFAULT')

      if ($app.core.auth.hasLicence()) {
         this.setUser()
         this._sub.add(
            this._store.select(getProject).subscribe((proj) => {
               if (!proj) {
                  this._store.dispatch(setProject({ projectId: sessionStorage.getItem('currentProjectId') }))
               } else {
                  this.setupUser()
                  this.setContentsData()
                  $app.layoutThemeCheck()
               }
            })
         )
      } else {
         this.accessDenied()
      }

      this._sub.add(
         $app.core.auth.licenceSubject$.subscribe((hasLicence) => {
            if (!hasLicence) {
               this.accessDenied()
            }
         })
      )
   }

   ngOnDestroy(): void {
      this._sub.unsubscribe()
   }

   setupUser() {
      if (this.console) console.log(this.logPrefix, 'setupUser')

      $app.core.auth.isUserLogged().then((value) => {
         if (value) {
            if (this.console) console.log(this.logPrefix, 'setupUser - logged')

            $app.core.auth.setAppRole('USER')
            $app.core.auth.setAppRole('PNO_ADMIN', [
               'editProject',
               'editPartner',
               'editEvent',
               'chooseEventCategory',
               'editDoodle',
               'editTopics',
               'editMlist',
               'editMlistHidden',
               'chooseMlistStatus',
            ])
            $app.core.auth.setAppRole('ADMIN', ['editProject', 'updatePartner', 'editEvent', 'chooseEventCategory', 'editMlist'])
            $app.core.auth.setAppRole('PARTNER')
            $app.core.auth.setAppRole('ECOFFICER')
            $app.core.auth.setAppRole('ADVISORY_BOARD')
            $app.core.auth.setAppRole('USER_GROUP')

            this.loadCurrentUser().then((userData: User) => {
               if (this.console) console.log(this.logPrefix, 'setupUser loadCurrentUser RETURN')

               if (this.console) console.log(this.logPrefix, 'setupUser loadCurrentUser RETURN', userData)

               if (!userData || !$app.core.user.setData(userData)) {
                  return
               }
               this.setUserAppRoles()
               this.setUserProjectRoles()
            })
         }
      })
   }

   setContentsData() {
      if (this.console) console.log(this.logPrefix, 'setContentsData')

      // Set - Events data
      $app.core.store.setData('eventCategories', [
         { id: 2, group: 1 },
         { id: 3, group: 1 },
         { id: 4, group: 1 },
         { id: 5, group: 1 },
         { id: 6, group: 1 },
         { id: 8, group: 7 },
         { id: 9, group: 7 },
         { id: 11, group: 10 },
      ])
   }

   //-- Current User --//

   loadCurrentUser() {
      if (this.console) console.log(this.logPrefix, 'loadCurrentUser')

      return new Promise((resolve, reject) => {
         $app.core.spinnerShow()
         this.userSrv.getCurrent().subscribe(
            (res) => {
               if (this.console) console.log(this.logPrefix, 'loadCurrentUser RESPONSE')

               if (!$app.core.code.isObject(res)) {
                  resolve(false)
               } else {
                  resolve(res)
               }

               $app.core.spinnerHide()
            },
            (error) => {
               resolve(false)
               $app.core.spinnerHide()
            }
         )
      })
   }

   //-- User Roles --//

   setUserAppRoles() {
      if (this.console) console.log(this.logPrefix, 'setUserAppRole')

      $app.core.auth.addRoleToUser($app.core.auth.isLicenceAdmin() ? 'PNO_ADMIN' : 'USER')
   }

   setUserProjectRoles() {
      if (this.console) console.log(this.logPrefix, 'setUserProjectRoles')

      if ($app.core.auth.rolesOnly(['USER'])) {
         $app.core.user.clearRoles()
         $app.core.auth.addRoleToUser('USER')

         const projectId = $app.getCurrentProjectId()
         if (projectId) {
            const authorities = $app.core.user.getDataParam('authorities')
            if (authorities && authorities.length) {
               const rolePrefix = 'ROLE_' + projectId + '#'

               for (let authority of authorities) {
                  if (authority.includes(rolePrefix)) {
                     const role = authority.replace(rolePrefix, '')

                     $app.core.auth.addRoleToUser(role) // set role by project
                     break
                  }
               }
            }
         }
      }
   }

   private setUser() {
      const mn = this.setUser.name

      if (this.console) console.log(this.logPrefix, 'setUser')

      from($app.core.auth.keycloakAngular.loadUserProfile())
         .pipe(
            filter((res) => res != null && res != undefined),
            tap((res) => console.log(this.logPrefix, mn, 'Keycloack User profile res:', res)),
            catchError((error) => {
               console.error(this.logPrefix, mn, 'Cannot retrive user info...')
               return of(error)
            })
         )
         .subscribe((user: KeycloakProfile) => {
            this._store.dispatch(StageActions.setUser({ user: `${user.firstName} ${user.lastName}` }))
         })
   }

   private accessDenied() {
      if (this.console) console.log(this.logPrefix, 'user does not have licence...')
      $app.core.goToSearchProjects()
   }
}

